/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { Controller, useFormContext } from "react-hook-form";
import { Icon, Tooltip } from "@mui/material";

const filter = createFilterOptions();

function SingleSelectDropdown({
  setting,
  setParentState,
  styles = {},
  name,
  validations = {},
  allowUserInput = false,
  disabled = false,
  id = null,
  multiline = false,
}) {
  const [autocompleteId] = useState(id || Math.floor(Math.random() * 100000));
  const {
    formState: { errors },
    setValue,
    trigger,
    control,
  } = useFormContext();

  const autoCompleteComponent = (field = {}) => {
    const settingOptions = setting.options;
    const [lowercasedOptionsDict, setLowercasedOptionsDict] = useState({});

    useEffect(() => {
      setLowercasedOptionsDict(
        settingOptions.reduce((lowercaseDict, option) => {
          lowercaseDict[option.display] = option.display.toLowerCase();
          return lowercaseDict;
        }, {})
      );
    }, [settingOptions]);

    return (
      <Autocomplete
        {...field}
        disabled={disabled}
        freeSolo={allowUserInput}
        id={autocompleteId}
        sx={{ width: "100%", marginBottom: "2rem", ...styles }}
        value={setting.field_value}
        clearOnBlur={allowUserInput}
        onChange={(event, selectedOption) => {
          setParentState(selectedOption || { value: "", display: "" });
          if (name) {
            // If we pass an object it will break
            setValue(name, selectedOption?.value ? selectedOption.value : selectedOption);
            trigger(name);
          }
        }}
        filterOptions={
          allowUserInput
            ? (options, params) => {
                const filtered = filter(options, params);

                const { inputValue } = params;
                // Suggest the creation of a new value
                const isExisting = options.some((option) => inputValue === option.value);
                if (inputValue !== "" && !isExisting) {
                  filtered.push({
                    display: inputValue,
                    value: inputValue,
                  });
                }

                return filtered;
              }
            : (allOptions, state) => {
                const currInputVal = state?.inputValue?.toLowerCase?.();
                return allOptions.filter((option) => {
                  if (!currInputVal) return true;
                  return lowercasedOptionsDict[option.display].indexOf(currInputVal) !== -1;
                });
              }
        }
        isOptionEqualToValue={(opt, value) => (value ? value === opt : true)}
        filterSelectedOptions={!!setting?.filterSelectedOptions}
        options={setting.options}
        groupBy={(option) => option.group}
        autoHighlight
        getOptionLabel={(option) => option.display}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            sx={{
              "& > img": { mr: 2, flexShrink: 0 },
              backgroundColor: setting?.field_value?.value === option.value ? "#8ba5ce96" : "white",
            }}
            key={`${option.display}.${option.value}`}
          >
            {option.display}
            {option.icon ? (
              option.iconTooltip ? (
                <Tooltip title={option.iconTooltip}>
                  <Icon
                    fontSize="small"
                    style={{ color: option.iconColor }}
                    sx={{
                      cursor: "pointer",
                      marginLeft: "1rem",
                    }}
                  >
                    {option.icon}
                  </Icon>
                </Tooltip>
              ) : (
                <Icon
                  fontSize="small"
                  color={option.iconColor}
                  sx={{
                    cursor: "pointer",
                    marginLeft: "1rem",
                  }}
                >
                  {option.icon}
                </Icon>
              )
            ) : null}
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={setting.en_field_label}
            inputProps={{
              ...params.inputProps,
            }}
            multiline={multiline}
            error={errors[name]?.message || false}
            helperText={errors[name]?.message}
          />
        )}
      />
    );
  };

  return name ? (
    <Controller
      name={name}
      control={control}
      defaultValue={setting?.field_value || null}
      rules={validations}
      render={({ field }) => autoCompleteComponent(field)}
    />
  ) : (
    autoCompleteComponent()
  );
}

export default SingleSelectDropdown;
